import { Button, Col, Row, Tooltip, Typography, message } from 'antd';
import React, { Component } from 'react';
import { filter, findIndex, forEach, isEmpty, pick, uniq } from 'lodash';
import { inject, observer } from 'mobx-react';

import { DownloadOutlined, SyncOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import HttpConstants from '../constants/HttpConstants';
import RoleConstants from '../constants/RoleConstants';
import POTable from './shared/poScreens/POTable';
import PoFilterComponent from '../app/components/filter/POFilter';
import RouteConstants from '../constants/RouteConstants';
import SecureComponent from '../app/components/SecureComponent';
import UrlConstants from '../api/UrlConstants';
import { downloadExcelForPO } from '../utils/excelUtils';
import { getUiUrlWithPathParams } from '../api/UrlGenerator';
import { makeApiCallWithAuthentication } from '../api/ApiManager';
import { withRouter } from 'react-router';

@inject('rootStore')
@observer
class POActiveList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      inventoryList: [],
      visibleInventorylist: [],
      holdTypeList: [],
      locations: [],
      pos: {},
      fins: {},
      vendors: {},
      containers: {},
      isSuperUser: false,
    };
  }

  componentDidMount = () => {
    this.fetchActivePOs();
    const roles = this.props.rootStore.userStore.roles;
    if (findIndex(roles, { name: RoleConstants.SUPER_USER }) > -1)
      this.setState({ isSuperUser: true });
    else this.setState({ isSuperUser: false });
  };

  fetchActivePOs = () => {
    this.setState({ loading: true });
    makeApiCallWithAuthentication(
      UrlConstants.GET_ALL_PO_ACTIVE.USECASE,
      HttpConstants.GET_METHOD
    )
      .then((response) => {
        if (response.status === 200) {
          this.setState({
            loading: false,
            inventoryList: response.data.po_list,
            visibleInventorylist: response.data.po_list,
            holdTypeList: response.data.hold_type_list,
          });
          this.processDataForFilters(response.data.po_list);
        } else throw response;
      })
      .catch((err) => {
        console.log('*****', err);
        this.setState({ loading: false });
        message.error("Couldn't get purchase orders, please try later");
      });
  };

  refreshPOs = () => {
    this.setState({ loading: true });
    makeApiCallWithAuthentication(UrlConstants.REFRESH_POS.USECASE, HttpConstants.GET_METHOD).then(
      (response) => {
        console.log('Response Received', response);
        if (response.status === 200) {
          setTimeout(() => {
            this.fetchActivePOs();
            message.success('POs Refereshed');
          }, 4500);
        } else throw response;
      })
      .catch((err) => {
        console.error('Error', err);
        this.setState({ loading: false });
        message.warn('Cannot refresh POs at the moment');
      });
  };

  processDataForFilters = (inventoryList) => {
    let locations = inventoryList.map((i) => i.location);
    locations = uniq(locations);

    const pos = {},
      fins = {},
      vendors = {},
      containers = {};

    inventoryList.forEach((i) => {
      if (!pos[i.po_number]) {
        pos[i.po_number] = [];
      }
      uniq(pos[i.po_number].push(i.location));

      i.po_fins.forEach((f) => {
        //Fin Filter
        if (!fins[f.fin]) fins[f.fin] = [];
        uniq(
          fins[f.fin].push({
            location: i.location,
            po_number: i.po_number,
          })
        );

        //Vendor Filter
        if (!vendors[i.buy_from_vendor_name])
          vendors[i.buy_from_vendor_name] = [];
        uniq(
          vendors[i.buy_from_vendor_name].push({
            location: i.location,
            po_number: i.po_number,
            fin: f.fin,
          })
        );

        //Container Filter
        if (!containers[i.container_no]) containers[i.container_no] = [];
        uniq(
          containers[i.container_no].push({
            location: i.location,
            po_number: i.po_number,
            fin: f.fin,
            vendor: i.buy_from_vendor_name,
          })
        );
      });
    });

    this.setState({
      locations,
      pos,
      fins,
      vendors,
      containers,
    });
  };

  applyFilter = (data) => {
    const { location, po, fin, vendor, container } = data;
    const { inventoryList } = this.state;

    this.setState({ loading: true });
    let filter_ = {};
    if (!isEmpty(location)) filter_.location = location;
    if (!isEmpty(po)) filter_.po_number = po;
    if (!isEmpty(container)) filter_.container_no = container;
    if (!isEmpty(vendor)) filter_.buy_from_vendor_name = vendor;

    let list = filter(inventoryList, filter_);

    if (!isEmpty(fin)) {
      const templist = [];
      forEach(list, function (l) {
        findIndex(l.po_fins, {
          fin: fin,
        }) > -1 && templist.push(l);
      });
      list = templist;
    }

    this.processDataForFilters(list);
    this.setState({ visibleInventorylist: list, loading: false });
  };

  resetFilter = () => {
    const { inventoryList } = this.state;
    this.processDataForFilters(inventoryList);
    this.setState({ visibleInventorylist: inventoryList });
  };

  downloadData = () => {
    const { visibleInventorylist } = this.state;
    let data = [];
    forEach(visibleInventorylist, function (i) {
      const obj = pick(i, [
        'po_number',
        'po_fins',
        'location',
        'container_no',
        'buy_from_vendor_name',
        'buy_from_country_code',
        'expected_receipt_date',
      ]);
      data.push(obj);
    });

    downloadExcelForPO(data, true);
  };

  navigateToPurchaseOrderDetail = (id) => {
    const url = getUiUrlWithPathParams(RouteConstants.PO_DETAILS, {
      id: id,
    });
    this.props.history.push(url);
  };

  render() {
    const {
      loading,
      visibleInventorylist,
      holdTypeList,
      locations,
      pos,
      fins,
      vendors,
      containers,
      isSuperUser,
    } = this.state;

    return (
      <div>
        <Helmet>
          <title>HMS - Purchase Orders Active List</title>
          <meta name='description' content='List of active purchase orders' />
        </Helmet>

        <Row className='space-top-10'>
          <Col xs={24} className='text-center'>
            <Typography.Text strong style={{ fontSize: 18 }}>
              Active Purchase Orders
            </Typography.Text>
          </Col>
          {process.env.REACT_APP_ENV !== 'production' && (
            <Col xs={24} style={{ textAlign: 'right' }} className='space-top-10'>
              <Button onClick={this.refreshPOs} disabled={loading}>
                <SyncOutlined spin={loading} />
                Refresh POs
              </Button>
            </Col>
          )}
          <Col xs={24}>
            <Row className='space-top' gutter={[8, 8]}>
              <Col xs={24} md={23}>
                <PoFilterComponent
                  locations={locations}
                  pos={pos}
                  fins={fins}
                  vendors={vendors}
                  containers={containers}
                  onSubmit={this.applyFilter}
                  onReset={this.resetFilter}
                />
              </Col>
              <Col xs={0} sm={24} md={1} style={{ textAlign: 'end' }}>
                <Tooltip title='Export as Excel'>
                  <Button
                    shape='circle'
                    onClick={this.downloadData}
                    disabled={loading || isEmpty(visibleInventorylist)}
                  >
                    <DownloadOutlined />
                  </Button>
                </Tooltip>
              </Col>
            </Row>
            <Row className='space-top'>
              <Col xs={24}>
                <POTable
                  data={visibleInventorylist}
                  handleViewDetails={this.navigateToPurchaseOrderDetail}
                  loading={loading}
                  permissions={this.props.rootStore.userStore.permissions}
                  reloadData={this.fetchActivePOs}
                  holdTypes={holdTypeList}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  }
}

export default SecureComponent(withRouter(POActiveList));
